import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { TemplateCard } from '../components/TemplateCard';
import { categoriesApi, templatesApi } from '../lib/api';
import { Search, X, SlidersHorizontal } from 'lucide-react';

export default function Templates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  
  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesApi.getAll();
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedCategory) params.category = selectedCategory;
        if (searchQuery) params.search = searchQuery;
        
        const response = await templatesApi.getAll(params);
        setTemplates(response.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchTemplates, 300);
    return () => clearTimeout(debounce);
  }, [selectedCategory, searchQuery]);

  const handleCategoryClick = (slug) => {
    const newParams = new URLSearchParams(searchParams);
    if (slug === selectedCategory) {
      newParams.delete('category');
    } else {
      newParams.set('category', slug);
    }
    setSearchParams(newParams);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    const newParams = new URLSearchParams(searchParams);
    if (e.target.value) {
      newParams.set('search', e.target.value);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  const selectedCategoryData = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="templates-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-4">
            {selectedCategoryData ? selectedCategoryData.name : 'All Templates'}
          </h1>
          <p className="text-lg text-[#5C6B67]">
            {selectedCategoryData 
              ? selectedCategoryData.description 
              : 'Browse our complete library of nonprofit templates.'}
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 h-12 bg-white border-[#E2E8E6] focus:border-primary-300 search-input"
              data-testid="template-search"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('search');
                  setSearchParams(newParams);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-[#5C6B67]"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-[#5C6B67]" />
            <Button
              variant={!selectedCategory ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategoryClick('')}
              className={!selectedCategory 
                ? "bg-primary text-white hover:bg-primary-800" 
                : "border-[#E2E8E6] text-[#5C6B67] hover:bg-[#FAFAFA]"}
              data-testid="filter-all"
            >
              All
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.slug ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryClick(category.slug)}
                className={selectedCategory === category.slug 
                  ? "bg-primary text-white hover:bg-primary-800" 
                  : "border-[#E2E8E6] text-[#5C6B67] hover:bg-[#FAFAFA]"}
                data-testid={`filter-${category.slug}`}
              >
                {category.name}
              </Button>
            ))}
          </div>

          {/* Active Filters */}
          {(selectedCategory || searchQuery) && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5C6B67]">Active filters:</span>
              {selectedCategory && (
                <Badge 
                  variant="secondary" 
                  className="bg-primary-50 text-primary hover:bg-primary-100 cursor-pointer"
                  onClick={() => handleCategoryClick('')}
                >
                  {selectedCategoryData?.name}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {searchQuery && (
                <Badge 
                  variant="secondary" 
                  className="bg-primary-50 text-primary hover:bg-primary-100 cursor-pointer"
                  onClick={() => {
                    setSearchQuery('');
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('search');
                    setSearchParams(newParams);
                  }}
                >
                  "{searchQuery}"
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              <button 
                onClick={clearFilters}
                className="text-sm text-[#5C6B67] hover:text-primary underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl h-64 animate-pulse" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No templates found</h3>
            <p className="text-[#5C6B67] mb-4">Try adjusting your search or filters.</p>
            <Button 
              variant="outline" 
              onClick={clearFilters}
              className="border-primary-200 text-primary hover:bg-primary-50"
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#5C6B67] mb-6">
              Showing {templates.length} template{templates.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {templates.map((template, index) => (
                <TemplateCard key={template.id} template={template} index={index} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
